/**
 * OCT28 WP-1 (FD-2026-10-28-A/-B) — HOW A TICKET TAKES PART IN THE TECHNICIAN'S SCHEDULE. Pure; no imports.
 *
 * Three questions, kept apart on purpose (founder, 2026-10-28: "Do not collapse these into one boolean"):
 *
 *   1. HANDS-ON NOW            is the technician performing hands-on work on it right now?   → isHandsOnNow
 *   2. HOLDS A RESERVATION     does it still hold an authoritative technician scheduling window?
 *                              Not decided here. Its authority is unchanged by WP-1: the booking guard
 *                              (`__validate_ticket_placement` / `__ticket_window_is_bookable`, SQL) and its Edge
 *                              mirror (`scheduleConflictCapacity.ts`). A paused or parked repair KEEPS its
 *                              reservations until the dedicated release transition (WP-5) says otherwise.
 *   3. PAUSE PRESSURE          may a paused ticket accrue Pause AutoBump time / be held to EOD reconciliation?
 *                              → isPausePressureEligible
 *
 * Rule 3 (Bucket 3 §3.7.1 :600 "Bay-blocking Pause AutoBump stops" when bay work moves into parking; §3.4.6 :310
 * "does not extend bay-based OptiFinish while physically out of the bay"; CORE-AV :80): paused, started work accrues
 * pause pressure only while it is NOT parked. "Parked" needs positive evidence (the lot flag or a spot), exactly as
 * `ticketProductionState.isParked` reads it. A missing bay alone is NOT parked: shops that do not model bays keep
 * today's behaviour.
 *
 * The SQL twin is `public.ticket_is_parked` / `public.ticket_pause_pressure_eligible`
 * (20270213000000_oct28_wp1_pause_pressure_eligibility.sql). Both are asserted against the same truth table
 * (tests/fixtures/oct28/pause_pressure_matrix.json): the unit test for this file, the SQL proof for the database.
 */

export interface ParticipationTicket {
  timer_status?: string | null;
  actually_started?: boolean | null;
  parking_lot?: boolean | null;
  parking_spot_id?: string | null;
}

/** Physically parked: the lot flag or an assigned spot (ticketProductionState.isParked). */
export function isTicketParked(t: ParticipationTicket): boolean {
  return t.parking_lot === true || (t.parking_spot_id != null && String(t.parking_spot_id).length > 0);
}

/** Question 1: the technician is working on it right now (a running timer). */
export function isHandsOnNow(t: ParticipationTicket): boolean {
  return t.timer_status === 'running';
}

/** Question 3: a paused ticket that may accrue Pause AutoBump time and is held to EOD reconciliation. */
export function isPausePressureEligible(t: ParticipationTicket): boolean {
  return t.timer_status === 'paused' && !isTicketParked(t);
}

/** Paused work that left the bay for the lot: no pause pressure, its reservations untouched (question 2 is not asked). */
export function isParkedPausedWork(t: ParticipationTicket): boolean {
  return t.timer_status === 'paused' && isTicketParked(t);
}

/** The finish derivations that ARE pause pressure (ticket-finish-intent / run-long-autobump vocabulary). */
export const PAUSE_PRESSURE_INTENTS: ReadonlySet<string> = new Set(['pause_extend', 'switched_away', 'unattended_hold', 'eod_lock', 'pushback']);

/**
 * Why the server refuses a pause-pressure derivation for this row, or null. One guard for every writer — the server
 * planner, the browser fallback hooks and a person's ticket-finish-intent request all reach the same derivation.
 * The refusal writes nothing: the row keeps its stored window (its reservation is question 2, not answered here).
 */
export function pausePressureRefusal(t: ParticipationTicket, intent: string): string | null {
  if (!PAUSE_PRESSURE_INTENTS.has(intent)) return null;
  return isParkedPausedWork(t) ? 'paused work is parked: no pause pressure (Bucket 3 :600)' : null;
}

/** OCT28 WP-2a: the derivations that would move a paused row's finish / pause watermark OUTSIDE the growth ledger. */
export const OUT_OF_LEDGER_PAUSE_INTENTS: ReadonlySet<string> = new Set(['pause_extend', 'switched_away', 'unattended_hold', 'pushback']);

/**
 * OCT28 WP-2a — a paused chain source (a live rollover continuation follows it) is accounted ONLY through the
 * run-long/pause growth ledger (claim_run_long_chain_growth: today locked at its boundary, the paused minutes join the
 * continuation once). Any other derivation of its finish is refused, whoever asks: the server planner, the browser
 * fallback, a person's ticket-finish-intent request, or a stale reconcile. The database enforces the same rule on the
 * pause watermark (tg_pause_watermark_ledger_owned, 20270213010000).
 */
export function pausedChainSourceRefusal(t: ParticipationTicket, intent: string, hasLiveContinuation: boolean): string | null {
  if (!OUT_OF_LEDGER_PAUSE_INTENTS.has(intent) || t.timer_status !== 'paused' || !hasLiveContinuation) return null;
  return 'paused chain source: its paused minutes are accounted only through the growth ledger';
}
